import { createBrowserRouter, Navigate, Routes, Route } from 'react-router';
import { RootLayout } from './layouts/RootLayout';
import { LandingLayout } from './layouts/LandingLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { OrgAdminLayout } from './layouts/OrgAdminLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useSubdomain } from './hooks/useSubdomain';

// Pages
import { HomePage } from './pages/HomePage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { SetupPage } from './pages/SetupPage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminBuildingsPage } from './pages/admin/BuildingsPage';
import { AdminResidentsPage } from './pages/admin/ResidentsPage';
import { AdminOrganizationsPage } from './pages/admin/OrganizationsPage';
import { AdminAdminsPage } from './pages/admin/AdminsPage';
import { OrgAdminBuildingsPage } from './pages/org-admin/BuildingsPage';
import { OrgAdminUnitsPage } from './pages/org-admin/UnitsPage';
import { OrgAdminResidentsPage } from './pages/org-admin/ResidentsPage';
import { AdminLoginPage } from './pages/admin/LoginPage';

// App Routes component that handles subdomain-based routing
function AppRoutes() {
  const { isLandingPage, isValidSubdomain } = useSubdomain();

  // Handle invalid subdomains
  if (!isValidSubdomain) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-muted-foreground">Invalid subdomain</p>
          <a href={`https://${import.meta.env.VITE_ROOT_DOMAIN || 'unidom.az'}`} className="text-primary hover:underline">
            Go to homepage
          </a>
        </div>
      </div>
    );
  }

  // Landing page routes (root domain or www)
  if (isLandingPage) {
    return (
      <Routes>
        <Route path="/" element={<LandingLayout />}>
          <Route index element={<LandingPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Residences subdomain - show app routes
  return (
    <Routes>
      <Route path="/" element={<RootLayout />}>
        <Route index element={<HomePage />} />
        <Route element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="setup" element={<SetupPage />} />
        </Route>
        
        <Route element={<ProtectedRoute allowedRoles={['resident']} />}>
          <Route path="dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardPage />} />
          </Route>
        </Route>
        
        <Route path="admin/login" element={<AdminLoginPage />} />
        
        <Route element={<ProtectedRoute allowedRoles={['system_admin', 'building_admin']} />}>
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="buildings" element={<AdminBuildingsPage />} />
            <Route path="residents" element={<AdminResidentsPage />} />
            <Route path="organizations" element={<AdminOrganizationsPage />} />
            <Route path="admins" element={<AdminAdminsPage />} />
          </Route>
        </Route>
        
        <Route element={<ProtectedRoute allowedRoles={['system_admin', 'building_admin']} />}>
          <Route path="admin/dashboard" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
          </Route>
        </Route>
        
        <Route element={<ProtectedRoute allowedRoles={['organization_admin']} />}>
          <Route path="org-admin" element={<OrgAdminLayout />}>
            <Route path="buildings" element={<OrgAdminBuildingsPage />} />
            <Route path="units" element={<OrgAdminUnitsPage />} />
            <Route path="residents" element={<OrgAdminResidentsPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

export const router = createBrowserRouter([
  {
    path: '*',
    element: <AppRoutes />,
  },
]);
